// Los EXTRAS del Chat debajo de una respuesta del agente: la tarjeta de una respuesta
// gráfica y los chips de sugerencia — ADR-2026-09-08-el-chat-toma-el-motor-del-asistente.
//
// La burbuja en sí (texto en markdown, tarjetas de generación, pickers, galería,
// soporte) la dibuja `MessageRow` de `ChatPanelV2`, la misma del asistente. Acá queda lo
// que el asistente no tiene. Lo montan el panel lateral y la pantalla dedicada, así que
// no hay forma de que se desincronicen entre las dos vistas.

import { useState } from 'react';
import { BarChart3, CalendarDays, CornerDownRight, Maximize2, Sparkles, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SubscriptionLimitModal from '../../components/SubscriptionLimitModal';
import type { V2ChatMessage } from '../../pages/v2/useV2Chat';
import type { GraficoChat } from '../tipos';

/** Dónde se puede dibujar el gráfico desde esta vista.
 *
 *  `'panel-derecho'` — pantalla dedicada (`/chat`): hay ancho, la tarjeta lo abre en la
 *  columna de la derecha sin moverse de la conversación.
 *  `'ir-a-pantalla'` — panel lateral: la franja mide ~420px, no entra un gráfico. La
 *  tarjeta avisa y manda a la pantalla completa. */
export type VistaGrafico = 'panel-derecho' | 'ir-a-pantalla';

/**
 * La tarjeta de una respuesta gráfica.
 *
 * OJO: acá NO se dibuja el HTML del modelo, ni un preview, ni una miniatura. Ese HTML
 * solo se renderiza dentro del `<iframe sandbox>` de `VisorGrafico` (ver
 * `documentoGrafico.ts` para el porqué). Esta tarjeta es texto plano de Atlas: título e
 * invitación a abrirlo.
 */
const TarjetaGrafico: React.FC<{
  grafico: GraficoChat;
  vista: VistaGrafico;
  activo: boolean;
  onAbrir?: (id: string) => void;
}> = ({ grafico, vista, activo, onAbrir }) => {
  const enPantalla = vista === 'ir-a-pantalla';
  return (
    <button
      type="button"
      onClick={() => onAbrir?.(grafico.id)}
      aria-pressed={activo}
      // Radios concéntricos: contenedor 16px con 8px de padding ⇒ el ícono interior va
      // en 8px.
      className={`group flex w-full items-center gap-3 rounded-2xl border bg-white p-2 pr-3 text-left transition-colors ${
        activo
          ? 'border-[#1644EB]/[0.45] bg-[#1644EB]/[0.04]'
          : 'border-[#02062B]/[0.1] hover:border-[#1644EB]/[0.35]'
      }`}
    >
      <span
        className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
          activo ? 'bg-[#1644EB]/[0.12] text-brand-primary' : 'bg-[#F0F2F7] text-[#02062B]/[0.5]'
        }`}
      >
        <BarChart3 className="h-5 w-5" aria-hidden="true" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-medium text-brand-black">
          {grafico.titulo}
        </span>
        <span className="block truncate text-[12px] text-[#02062B]/[0.5]">
          {enPantalla
            ? 'Abrilo en pantalla completa para verlo'
            : activo
              ? 'Se está mostrando a la derecha'
              : 'Tocá para verlo a la derecha'}
        </span>
      </span>

      {enPantalla ? (
        <Maximize2
          className="h-4 w-4 shrink-0 text-[#02062B]/[0.4] transition-colors group-hover:text-brand-primary"
          aria-hidden="true"
        />
      ) : null}
    </button>
  );
};

/**
 * El botón de desbloquear videos: lo cuelga `ofrecer_desbloquear_videos` (o la red de las
 * herramientas de video) cuando el plan no los incluye — ADR-2026-10-12-el-agente-es-de-todos.
 * Lleva a los planes, que es donde se paga.
 */
const TarjetaDesbloquearVideos: React.FC = () => {
  const navigate = useNavigate();
  return (
    // Radios concéntricos: contenedor 16px con 8px de padding ⇒ ícono y botón en 8px.
    <div className="flex w-full max-w-[420px] items-center gap-3 rounded-2xl border border-[#1644EB]/[0.2] bg-[#1644EB]/[0.04] p-2">
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#1644EB]/[0.1] text-brand-primary">
        <Video className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-semibold text-brand-black">Videos con los planes Pro y Ultra</span>
        <span className="block text-[12px] text-[#02062B]/[0.55]">Pasate de plan para crearlos desde el chat</span>
      </span>
      <button
        type="button"
        onClick={() => navigate('/subscription')}
        className="shrink-0 rounded-lg bg-brand-primary px-4 py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover"
      >
        Desbloquear videos
      </button>
    </div>
  );
};

/**
 * El botón de sumar créditos: lo cuelga `ofrecer_sumar_creditos` cuando lo que pide no entra
 * en el saldo (ADR-2026-10-12-sin-creditos-en-el-chat). Abre el MISMO catálogo de planes y
 * créditos que el resto de la app cuando se acaba el saldo.
 */
/**
 * Ir al calendario de Instagram después de que `mandar_a_instagram` armó un post
 * (ADR-2026-10-13-mandar-a-instagram-desde-el-chat). Misma anatomía que la de desbloquear
 * videos, para que se lea como una acción de Atlas y no como texto del modelo.
 */
const BotonVerCalendario: React.FC = () => {
  const navigate = useNavigate();
  return (
    // Radios concéntricos: contenedor 16px con 8px de padding ⇒ ícono y botón en 8px.
    <div className="flex w-full max-w-[420px] items-center gap-3 rounded-2xl border border-[#1644EB]/[0.2] bg-[#1644EB]/[0.04] p-2">
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#1644EB]/[0.1] text-brand-primary">
        <CalendarDays className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-semibold text-brand-black">Quedó en tu calendario de Instagram</span>
        <span className="block text-[12px] text-[#02062B]/[0.55]">Planificado, todavía sin publicar</span>
      </span>
      <button
        type="button"
        onClick={() => navigate('/instagram')}
        className="shrink-0 rounded-lg bg-brand-primary px-4 py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover"
      >
        Ver en el calendario
      </button>
    </div>
  );
};

const TarjetaSumarCreditos: React.FC = () => {
  const [abierto, setAbierto] = useState(false);
  return (
    // Radios concéntricos: contenedor 16px con 8px de padding ⇒ ícono y botón en 8px.
    <div className="flex w-full max-w-[420px] items-center gap-3 rounded-2xl border border-[#1644EB]/[0.2] bg-[#1644EB]/[0.04] p-2">
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#1644EB]/[0.1] text-brand-primary">
        <Sparkles className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-semibold text-brand-black">Te quedaste sin créditos</span>
        <span className="block text-[12px] text-[#02062B]/[0.55]">Sumá créditos o pasate de plan para seguir</span>
      </span>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="shrink-0 rounded-lg bg-brand-primary px-4 py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover"
      >
        Sumar créditos
      </button>
      <SubscriptionLimitModal isOpen={abierto} onClose={() => setAbierto(false)} type="plan-limit-reached" />
    </div>
  );
};

interface Props {
  mensaje: V2ChatMessage;
  /**
   * Las sugerencias a dibujar debajo de ESTE mensaje. No se leen del mensaje: las del
   * turno van siempre después del último mensaje del asistente, decida lo que decida la
   * herramienta sobre dónde colgarlas (`sugerenciasDelTurno`,
   * ADR-2026-10-05-las-sugerencias-van-al-final-del-turno).
   */
  sugerencias?: string[];
  /** Se llama al tocar una sugerencia. */
  onSugerencia?: (texto: string) => void;
  /** Con el turno en curso las sugerencias no se pueden tocar. */
  bloqueado?: boolean;
  /** Qué hace la tarjeta de un gráfico en esta vista. */
  vistaGrafico?: VistaGrafico;
  /** Se llama al tocar la tarjeta de un gráfico, con su id. */
  onAbrirGrafico?: (id: string) => void;
  /** Gráfico que se está mirando, para marcarlo en la lista. */
  graficoActivoId?: string | null;
}

export const ExtrasBurbuja: React.FC<Props> = ({
  mensaje,
  sugerencias = [],
  onSugerencia,
  bloqueado,
  vistaGrafico = 'ir-a-pantalla',
  onAbrirGrafico,
  graficoActivoId,
}) => {
  const graficos = mensaje.graficos ?? [];
  // Sin nada que dibujar no se dibuja NADA: un contenedor vacío dentro de una lista
  // con `gap` es un hueco que el usuario lee como un error.
  const desbloquearVideos = mensaje.desbloquear === 'videos';
  const sumarCreditos = mensaje.desbloquear === 'creditos';
  const verCalendario = mensaje.verCalendario === true;
  if (
    graficos.length === 0 &&
    sugerencias.length === 0 &&
    !desbloquearVideos &&
    !sumarCreditos &&
    !verCalendario
  )
    return null;

  return (
    <div className="flex flex-col gap-3">
      {/* El HTML del modelo NO se toca acá: la tarjeta es un botón de Atlas y el dibujo
          pasa siempre por `VisorGrafico`. */}
      {graficos.map((g) => (
        <TarjetaGrafico
          key={g.id}
          grafico={g}
          vista={vistaGrafico}
          activo={vistaGrafico === 'panel-derecho' && graficoActivoId === g.id}
          onAbrir={onAbrirGrafico}
        />
      ))}

      {desbloquearVideos && <TarjetaDesbloquearVideos />}
      {sumarCreditos && <TarjetaSumarCreditos />}
      {verCalendario && <BotonVerCalendario />}

      {/* Una lista, no pills: un rótulo chico y una fila por sugerencia con la flecha de
          "seguir por acá" (ADR-2026-10-05-las-sugerencias-van-al-final-del-turno). Sin
          borde ni fondo propio: es la continuación de la respuesta, no un formulario. */}
      {sugerencias.length > 0 && (
        <div className="flex flex-col pt-1" role="group" aria-label="Próximos pasos">
          <p className="px-1.5 pb-1 text-[12px] font-medium text-[#02062B]/[0.45]">Próximos pasos</p>
          {sugerencias.map((s, i) => (
            <button
              key={`${i}-${s}`}
              type="button"
              disabled={bloqueado}
              onClick={() => onSugerencia?.(s)}
              className="group flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left text-[14px] text-brand-black transition-colors hover:bg-[#02062B]/[0.04] hover:text-brand-primary disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-brand-black"
            >
              <CornerDownRight
                className="h-4 w-4 shrink-0 text-[#02062B]/[0.35] transition-colors group-hover:text-brand-primary group-disabled:text-[#02062B]/[0.35]"
                strokeWidth={2}
                aria-hidden="true"
              />
              <span className="min-w-0">{s}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExtrasBurbuja;
